import { api, setAccessToken, unwrap } from './api';

export interface ImpersonationStartResponse {
  session_id: string;
  handoff_token: string;
  handoff_url: string;
  target_user_id: string;
  expires_at: string;
}

export interface ImpersonationConsumeResponse {
  access_token: string;
  token_type: string;
  expires_at: string;
  actor_type: 'tenant';
  tenant_id: string;
}

export interface ImpersonationBanner {
  session_id: string;
  target_user_id: string;
  target_email: string;
  real_actor_id: string;
  reason: string;
  started_at: string;
  expires_at: string;
}

export async function startBackofficeImpersonation(
  targetUserId: string,
  tenantId: string,
  reason: string,
): Promise<ImpersonationStartResponse> {
  return unwrap<ImpersonationStartResponse>(
    api.post(`/backoffice/users/${targetUserId}/impersonate`, {
      tenant_id: tenantId,
      reason,
    }),
  );
}

export async function startTenantImpersonation(
  targetUserId: string,
  reason: string,
): Promise<ImpersonationStartResponse> {
  return unwrap<ImpersonationStartResponse>(
    api.post(`/accounts/users/${targetUserId}/impersonate`, { reason }),
  );
}

export async function consumeImpersonationHandoff(
  token: string,
): Promise<ImpersonationConsumeResponse> {
  const tokens = await unwrap<ImpersonationConsumeResponse>(
    api.post('/auth/impersonate/consume', { token }, { _skipAuthRetry: true } as never),
  );
  setAccessToken(tokens.access_token);
  return tokens;
}

export async function fetchImpersonationBanner(): Promise<ImpersonationBanner | null> {
  try {
    return await unwrap<ImpersonationBanner>(api.get('/auth/impersonation/banner'));
  } catch {
    return null;
  }
}

export async function stopImpersonation(): Promise<void> {
  await api.post('/auth/impersonate/stop').catch(() => undefined);
}

export interface AdminStopResponse {
  stopped: boolean;
  session_id: string | null;
}

/**
 * Admin-side recovery: clears the operator's active impersonation row
 * (e.g. when the handoff popup was closed before consume). Idempotent —
 * returns `stopped=false` when nothing was active.
 */
export async function stopBackofficeImpersonation(): Promise<AdminStopResponse> {
  return unwrap<AdminStopResponse>(api.post('/backoffice/impersonation/stop'));
}
